import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Upload, X, ExternalLink } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { captureSystemError } from '../utils/errorReporting';

interface MemberProfile {
  name: string;
  photo_name: string;
  email: string;
  phone_number: string;
  job_title: string;
  company: string;
  location: string;
  linkedin_url: string;
  public_bio: string;
  community_status?: string;
}

export function Profile() {
  const [profile, setProfile] = useState<MemberProfile>({
    name: '',
    photo_name: '',
    email: '',
    phone_number: '',
    job_title: '',
    company: '',
    location: '',
    linkedin_url: '',
    public_bio: '',
    community_status: ''
  });
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof MemberProfile, string>>>({});
  const customDomain = 'https://data.skip.community';

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (profile.photo_name) {
      setPreview(profile.photo_name);
    }
  }, [profile.photo_name]);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        const { data, error } = await supabase
          .from('members')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (error) throw error;
        if (data) {
          setProfile(data);
          if (data.photo_name) {
            setPreview(data.photo_name);
          }
        }
      }
    } catch (error: any) {
      captureSystemError(error, {
        operation: 'member_profile',
        action: 'load_profile',
        userId: (await supabase.auth.getUser()).data.user?.id,
      });
      setMessage({ type: 'error', text: error.message });
    } finally {
      setDataLoading(false);
    }
  };

  const validateForm = () => {
    const errors: Partial<Record<keyof MemberProfile, string>> = {};
    const requiredFields: (keyof MemberProfile)[] = [
      'name',
      'email',
      'phone_number',
      'job_title',
      'location',
      'linkedin_url'
    ];
    
    // Only require public_bio if community_status is 'active'
    if (profile.community_status === 'active') {
      requiredFields.push('public_bio');
    }

    requiredFields.forEach(field => {
      if (!profile[field]) {
        errors[field] = 'This field is required';
      }
    });

    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (profile.linkedin_url && !/^https?:\/\/[^\s/$.?#].[^\s]*$/.test(profile.linkedin_url)) {
      errors.linkedin_url = 'Please enter a valid URL';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name as keyof MemberProfile]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = e.target.files?.[0];
      if (!file) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      if (!profile.name) {
        throw new Error('Please enter your name before uploading a photo');
      }

      const fileExt = file.name.split('.').pop();
      const sanitizedName = profile.name.replace(/\s+/g, '-');
      const fileName = `${sanitizedName}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(fileName);

      const publicUrl = data.publicUrl.replace(/^https:\/\/[^/]+/, customDomain);

      setPreview(publicUrl);
      setProfile(prev => ({
        ...prev,
        photo_name: publicUrl
      }));

      const { error: updateError } = await supabase
        .from('members')
        .upsert({
          ...profile,
          photo_name: publicUrl,
          user_id: user.id,
          updated_at: new Date().toISOString()
        });

      if (updateError) throw updateError;

      setMessage({ type: 'success', text: 'Photo uploaded successfully!' });
    } catch (error: any) {
      captureSystemError(error, {
        operation: 'member_profile',
        action: 'photo_upload',
        userId: (await supabase.auth.getUser()).data.user?.id,
        metadata: {
          fileName: file?.name,
          fileSize: file?.size,
          fileType: file?.type,
        },
      });
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }
    
    try {
      setUploading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      if (profile.photo_name) {
        const fileName = profile.photo_name.split('/').pop();
        if (fileName) {
          const { error: deleteError } = await supabase.storage
            .from('profile-photos')
            .remove([fileName]);

          if (deleteError) throw deleteError;
        }
      }

      setPreview(null);
      setProfile(prev => ({
        ...prev,
        photo_name: ''
      }));

      const { error: updateError } = await supabase
        .from('members')
        .upsert({
          ...profile,
          photo_name: '',
          user_id: user.id,
          updated_at: new Date().toISOString()
        });

      if (updateError) throw updateError;

      setMessage({ type: 'success', text: 'Photo removed successfully!' });
    } catch (error: any) {
      captureSystemError(error, {
        operation: 'member_profile',
        action: 'photo_remove',
        userId: (await supabase.auth.getUser()).data.user?.id,
      });
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (loading) {
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    if (!validateForm()) {
      setMessage({ type: 'error', text: 'Please fill in all required fields correctly.' });
      setLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('members')
        .upsert({
          ...profile,
          user_id: user.id,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Get the user's session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      // Send email notification
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/profile-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ profile }),
      });

      if (!response.ok) {
        throw new Error('Failed to send email notification');
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error: any) {
      // Determine if it's a profile update error or email notification error
      const isEmailError = error.message.includes('email notification');
      
      captureSystemError(error, {
        operation: 'member_profile',
        action: isEmailError ? 'email_notification' : 'profile_update',
        userId: (await supabase.auth.getUser()).data.user?.id,
        metadata: {
          profileFields: Object.keys(profile).filter(key => profile[key as keyof MemberProfile]),
          errorContext: isEmailError ? 'edge_function' : 'database',
        },
      });
      
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return <LoadingSpinner message="Loading profile..." />;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Edit Profile</h1>
        {profile.name && profile.community_status === 'active' && (
          <a
            href={`https://skip.community/#${encodeURIComponent(profile.name)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            View Public Profile
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
      <div className="bg-white shadow rounded-lg p-6">
        {message && (
          <div
            className={`mb-6 p-4 rounded ${
              message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-center">
            <div className="relative">
              {preview ? (
                <div className="relative w-32 h-32">
                  <img
                    src={preview}
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center">
                  <label className="cursor-pointer flex flex-col items-center">
                    <Upload className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-500 mt-2">Upload Photo</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-full">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="name">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={profile.name}
                onChange={handleChange}
                required
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  formErrors.name
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="email">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={profile.email}
                onChange={handleChange}
                required
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  formErrors.email
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {formErrors.email && (
                <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="phone_number">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={profile.phone_number}
                onChange={handleChange}
                required
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  formErrors.phone_number
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {formErrors.phone_number && (
                <p className="mt-1 text-sm text-red-600">{formErrors.phone_number}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="job_title">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="job_title"
                name="job_title"
                value={profile.job_title}
                onChange={handleChange}
                required
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  formErrors.job_title
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {formErrors.job_title && (
                <p className="mt-1 text-sm text-red-600">{formErrors.job_title}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="company">
                Company
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={profile.company}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="location">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={profile.location}
                onChange={handleChange}
                required
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  formErrors.location
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {formErrors.location && (
                <p className="mt-1 text-sm text-red-600">{formErrors.location}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="linkedin_url">
                LinkedIn URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                id="linkedin_url"
                name="linkedin_url"
                value={profile.linkedin_url}
                onChange={handleChange}
                required
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  formErrors.linkedin_url
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {formErrors.linkedin_url && (
                <p className="mt-1 text-sm text-red-600">{formErrors.linkedin_url}</p>
              )}
            </div>
          </div>

          {profile.community_status === 'active' && (
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="public_bio">
                Public Bio <span className="text-red-500">*</span>
              </label>
              <textarea
                id="public_bio"
                name="public_bio"
                rows={4}
                value={profile.public_bio}
                onChange={handleChange}
                required
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  formErrors.public_bio
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {formErrors.public_bio && (
                <p className="mt-1 text-sm text-red-600">{formErrors.public_bio}</p>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <LoadingSpinner inline message="Saving..." /> : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}